import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { 
    uploadContract, 
    uploadSignature, 
    getContractsByClient, 
    getActiveContract, 
    getContractDownload, 
    archiveContract,
    sendSignatureLink,
    generateContract
} from '../api/contractService';
import { 
    Contract, 
    GetContractsParams, 
    DownloadParams,
    SendSignatureLinkRequest,
    getContractTypeName
} from '../../types/contract';
import { useTenantCode } from './useTenantCode';

// ========================================
// QUERY HOOKS
// ========================================

/**
 * Hook para obtener todos los contratos de un cliente
 */
export const useContractsByClientQuery = (
    clientId?: string, 
    params?: GetContractsParams
) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['contracts', 'client', clientId, params, tenantCode],
        queryFn: () => getContractsByClient(clientId!, params),
        enabled: !!clientId,
        staleTime: 1000 * 60 * 5,
    });
};

/**
 * Hook para obtener el contrato activo de un cliente
 */
export const useActiveContractQuery = (clientId?: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['contract', 'active', clientId, tenantCode],
        queryFn: () => getActiveContract(clientId!),
        enabled: !!clientId,
        staleTime: 1000 * 60 * 2,
        retry: false,
    });
};

/**
 * Hook para generar URL de descarga de contrato/firma
 */
export const useContractDownloadQuery = (
    contractId?: string, 
    params?: DownloadParams,
    options?: { enabled?: boolean }
) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['contract', 'download', contractId, params, tenantCode],
        queryFn: () => getContractDownload(contractId!, params),
        enabled: !!contractId && (options?.enabled ?? false),
        staleTime: 1000 * 60 * 1,
        refetchOnWindowFocus: false,
    });
};

// ========================================
// MUTATION HOOKS
// ========================================

/**
 * Mutación para subir un nuevo contrato
 */
export const useUploadContractMutation = () => useMutation({
    mutationFn: ({ 
        contractFile, 
        clientId, 
        subscriptionId, 
        createdBy 
    }: {
        contractFile: File;
        clientId: string;
        subscriptionId: string;
        createdBy: string;
    }) => uploadContract(contractFile, clientId, subscriptionId, createdBy),
    
    onSuccess: (_data, variables) => {
        // Invalidar contratos del cliente
        queryClient.invalidateQueries({ 
            queryKey: ['contracts', 'client', variables.clientId] 
        });
        
        // Invalidar contrato activo del cliente
        queryClient.invalidateQueries({ 
            queryKey: ['contract', 'active', variables.clientId] 
        });
        
        toast.success('Contrato subido exitosamente');
    },
    
    onError: (error: any) => {
        const message = error.response?.data?.message || 'Error al subir el contrato';
        toast.error(message);
    },
});

/**
 * Mutación para generar contrato automáticamente
 */
export const useGenerateContractMutation = () => useMutation({
    mutationFn: ({ 
        clientId, 
        subscriptionId, 
        createdBy 
    }: {
        clientId: string;
        subscriptionId: string;
        createdBy: string;
    }) => generateContract(clientId, subscriptionId, createdBy),
    
    onSuccess: (data, variables) => {
        // Invalidar contratos del cliente
        queryClient.invalidateQueries({ 
            queryKey: ['contracts', 'client', variables.clientId] 
        });
        
        // Invalidar contrato activo del cliente
        queryClient.invalidateQueries({ 
            queryKey: ['contract', 'active', variables.clientId] 
        });
        
        // Toast con tipo de contrato generado
        const typeName = getContractTypeName(data.contractType);
        toast.success(`Contrato generado: ${typeName}`);
    },
    
    onError: (error: any) => {
        const message = error.response?.data?.message || 'Error al generar contrato';
        toast.error(message);
    },
});

/**
 * Mutación para subir firma de contrato
 */
export const useUploadSignatureMutation = () => useMutation({
    mutationFn: ({ 
        contractId, 
        signatureFile, 
        firmanteDni, 
        updatedBy,
        clientEmail
    }: {
        contractId: string;
        signatureFile: File;
        firmanteDni: string;
        updatedBy: string;
        clientEmail?: string; // Opcional para envío de confirmación
    }) => uploadSignature(contractId, signatureFile, firmanteDni, updatedBy, clientEmail),
    
    onSuccess: (data, variables) => {
        // Obtener clientId del contrato actualizado
        const clientId = data.contract.cliente_id;
        
        // Invalidar contratos del cliente
        queryClient.invalidateQueries({ 
            queryKey: ['contracts', 'client', clientId] 
        });
        
        // Invalidar contrato activo del cliente
        queryClient.invalidateQueries({ 
            queryKey: ['contract', 'active', clientId] 
        });
        
        // Invalidar descargas del contrato específico
        queryClient.invalidateQueries({ 
            queryKey: ['contract', 'download', variables.contractId] 
        });
        
        toast.success('Firma subida exitosamente. Contrato firmado.');
    },
    
    onError: (error: any) => {
        const message = error.response?.data?.message || 'Error al subir la firma';
        toast.error(message);
    },
});

/**
 * Mutación para enviar link de firma por email
 */
export const useSendSignatureLinkMutation = () => useMutation({
    mutationFn: ({ 
        contractId, 
        request 
    }: {
        contractId: string;
        request: SendSignatureLinkRequest;
    }) => sendSignatureLink(contractId, request),
    
    onSuccess: (data, variables) => {
        // Invalidar queries para actualizar la UI inmediatamente
        // Invalidar el contrato específico
        queryClient.invalidateQueries({ queryKey: ['contracts', variables.contractId] });
        // Invalidar todos los contratos activos (para actualizar el que acabamos de modificar)
        queryClient.invalidateQueries({ queryKey: ['contract', 'active'] });
        // Invalidar lista de contratos por cliente
        queryClient.invalidateQueries({ queryKey: ['contracts', 'client'] });
        
        // Obtener si fue envío intencional al centro
        const envioIntencionalAlCentro = variables.request.sendToCentro === true;
        
        // Verificar si el link fue enviado al centro
        if (data.sentToCentro) {
            // Si fue envío INTENCIONAL al centro (usuario eligió botón "Enviar al Centro")
            if (envioIntencionalAlCentro) {
                toast.success(
                    `📧 Link enviado al centro\n` +
                    `Destino: ${data.sentTo}\n` +
                    `Contrato: ${data.firmanteNombre}`,
                    { autoClose: 5000 }
                );
            }
            // Si fue envío por FALLBACK (cliente sin email)
            else {
                // Caso 1: Una sola firma enviada al centro por falta de email
                if (!data.firmantesEnCentro || data.firmantesEnCentro.length === 0) {
                    toast.warning(
                        `⚠️ Cliente sin email registrado\n` +
                        `Link enviado al centro: ${data.sentTo}\n` +
                        `${data.firmanteNombre} debe firmar presencialmente`,
                        { autoClose: 8000 }
                    );
                } 
                // Caso 2: Múltiples firmas, al menos una al centro por falta de email
                else {
                    const firmantesTexto = data.firmantesEnCentro
                        .map(f => f === 'cliente' ? 'Cliente' : 'Pagador')
                        .join(' y ');
                    
                    toast.warning(
                        `⚠️ ${firmantesTexto} sin email registrado\n` +
                        `Link(s) enviados al centro: ${data.sentTo}\n` +
                        `Deben firmar presencialmente en el centro`,
                        { autoClose: 8000 }
                    );
                }
            }
        } else {
            // Caso normal: todos los firmantes tienen email y se envió correctamente
            toast.success(`✅ Link enviado a ${data.firmanteNombre}`);
        }
    },
    
    onError: (error: any) => {
        const message = error.response?.data?.message || 'Error al enviar link de firma';
        toast.error(message);
    },
});

/**
 * Mutación para archivar contrato
 */
export const useArchiveContractMutation = () => useMutation({
    mutationFn: ({ 
        contractId, 
        updatedBy, 
        reason 
    }: {
        contractId: string;
        updatedBy: string;
        reason?: string;
    }) => archiveContract(contractId, updatedBy, reason),
    
    onSuccess: (data, variables) => {
        // Obtener clientId del contrato archivado
        const clientId = data.cliente_id;
        
        // Invalidar contratos del cliente
        queryClient.invalidateQueries({ 
            queryKey: ['contracts', 'client', clientId] 
        });
        
        // Invalidar contrato activo del cliente
        queryClient.invalidateQueries({ 
            queryKey: ['contract', 'active', clientId] 
        });
        
        toast.success('Contrato archivado exitosamente');
    },
    
    onError: (error: any) => {
        const message = error.response?.data?.message || 'Error al archivar el contrato';
        toast.error(message);
    },
});

// ========================================
// UTILITY HOOKS
// ========================================

/**
 * Hook para invalidar manualmente las consultas de contratos de un cliente
 */
export const useInvalidateContractQueries = () => {
    return {
        invalidateByClient: (clientId: string) => {
            queryClient.invalidateQueries({ 
                queryKey: ['contracts', 'client', clientId] 
            });
            queryClient.invalidateQueries({ 
                queryKey: ['contract', 'active', clientId] 
            });
        },
        
        invalidateAll: () => {
            queryClient.invalidateQueries({ 
                queryKey: ['contracts'] 
            });
            queryClient.invalidateQueries({ 
                queryKey: ['contract'] 
            });
        }
    };
};